import { useState, useEffect, ReactNode } from 'react'
import { TChatHistory } from 'extra/types/Messages'
import { TStoredDecisionAnswer } from 'extra/types/Decisions'
import { JSONHelper } from 'extra/Helpers/JSONHelper'
import { Copy, Maximize2, X, Check, AlignLeft, Braces } from 'lucide-react'

function DataCellWrapper ({ rawData, children, expand, renderReadable }: { rawData: any, children: ReactNode, expand?: boolean, renderReadable?: ReactNode }) {
  if (expand) {
    return <div>  {children}</div>
  }
  const [copied, setCopied] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [viewMode, setViewMode] = useState<'readable' | 'json'>('readable')

  useEffect(() => {
    if (expanded) setViewMode('readable')
  }, [expanded])

  useEffect(() => {
    if (!expanded) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpanded(false)
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [expanded])

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation()
    const text = typeof rawData === 'string' ? rawData : JSON.stringify(rawData, null, 2)
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <>
      <div className="relative inline-block group/cell">
        {children}
        <div className="absolute top-1 right-1 z-10 hidden group-hover/cell:flex gap-1">
          <button
            onClick={handleCopy}
            className="p-1 bg-gray-700 hover:bg-gray-600 rounded text-gray-300 hover:text-white cursor-pointer"
            title="Copy JSON"
          >
            {copied
              ? <Check size={14} className="text-green-400"/>
              : <Copy size={14}/>
            }
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation(); setExpanded(true)
            }}
            className="p-1 bg-gray-700 hover:bg-gray-600 rounded text-gray-300 hover:text-white cursor-pointer"
            title="Expand"
          >
            <Maximize2 size={14}/>
          </button>
        </div>
      </div>
      {expanded && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setExpanded(false)}>
          <div className="relative bg-gray-900 border border-gray-700 rounded-lg p-4 w-[90vw] h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-3">
              <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded text-gray-300 hover:text-white text-xs cursor-pointer"
              >
                {copied
                  ? <>
                  <Check size={14} className="text-green-400"/>
                  Copied
                </>
                  : <>
                  <Copy size={14}/> Copy
                </>}
              </button>
              {renderReadable && (
                <div className="flex border border-gray-600 rounded overflow-hidden">
                  <button
                    onClick={() => setViewMode('readable')}
                    className={`flex items-center gap-1 px-2 py-1 text-xs cursor-pointer ${viewMode === 'readable' ? 'bg-gray-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}
                    title="Readable"
                  >
                    <AlignLeft size={14}/> Readable
                  </button>
                  <button
                    onClick={() => setViewMode('json')}
                    className={`flex items-center gap-1 px-2 py-1 text-xs cursor-pointer ${viewMode === 'json' ? 'bg-gray-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}
                    title="JSON"
                  >
                    <Braces size={14}/> JSON
                  </button>
                </div>
              )}
              <button
                onClick={() => setExpanded(false)}
                className="p-1 bg-gray-700 hover:bg-gray-600 rounded text-gray-300 hover:text-white cursor-pointer"
                title="Close"
              >
                <X size={16}/>
              </button>
            </div>
            {renderReadable && viewMode === 'readable'
              ? <div className="flex-1 overflow-auto custom-scrollbar p-2">{renderReadable}</div>
              : <pre className="flex-1 overflow-auto text-xs font-mono whitespace-pre-wrap break-all custom-scrollbar p-2">
                {typeof rawData === 'string' ? rawData : JSONHelper.unescapeNewlinesDeep(JSON.stringify(rawData, null, 2))}
              </pre>
            }
          </div>
        </div>
      )}
    </>
  )
}

export function ChatHistoryComponent ({ history, expand }: {
  history: TChatHistory | null | undefined,
  expand?: boolean
}) {
  const renderMessages = (isExpanded: boolean) => (
    <div className={`${!isExpanded ? 'overflow-auto max-w-96 min-w-72 max-h-64' : ''} text-xs font-mono flex flex-col ${isExpanded ? 'gap-2' : 'gap-1.5 p-1'}`}>
      {history
        ? history
          .map((m, i) => <div className={`${isExpanded ? 'p-3' : 'p-1.5'} rounded border border-gray-700`} key={i}>
              <span className="font-bold text-blue-400">{m.role}</span>
              <div className={`whitespace-pre-wrap ${isExpanded ? 'mt-1' : 'mt-0.5'}`}>{('content' in m && m.content) ? m.content as string : ''}</div>
              {('tool_calls' in m && m.tool_calls) ? <pre className="overflow-scroll mt-1">{JSON.stringify(m.tool_calls, null, 2)}</pre> : ''}
              {('toolName' in m && m.toolName) ? <pre className="overflow-scroll mt-1">{JSON.stringify(m.toolName, null, 2)}</pre> : ''}
            </div>,
          )
        : <>Empty</>
      }
    </div>
  )

  return (history?.length
    ? <DataCellWrapper rawData={history} expand={expand} renderReadable={renderMessages(true)}>
      {renderMessages(expand ?? false)}
    </DataCellWrapper>
    : <div>
        <span className="text-gray-400 italic">No history</span>
      </div>
  )
}

// a list cell re-renders on every keystroke in the search fields, and a request can hold 400 items
const MAX_CELL_ANSWERS = 20

export function DecisionAnswersComponent ({ answers, expand }: {
  answers: Array<TStoredDecisionAnswer>,
  expand?: boolean
}) {
  const renderAnswers = (isExpanded: boolean) => {
    const shown = isExpanded ? answers : answers.slice(0, MAX_CELL_ANSWERS)
    return (
      <div className={`${!isExpanded ? 'overflow-auto max-w-96 max-h-64' : ''} text-xs font-mono`}>
        <table className="text-left">
          <thead>
            <tr className="text-gray-400">
              <th className="pr-4 font-normal">Item</th>
              <th className="pr-4 font-normal">Choice</th>
              <th className="font-normal">Confidence</th>
            </tr>
          </thead>
          <tbody>
            {shown.map(a => (
              <tr key={a.id} className="border-t border-gray-700">
                <td className="pr-4 py-0.5 text-blue-400">{a.id}</td>
                {a.type === 'choice'
                  ? <>
                    <td className="pr-4 py-0.5">{a.choice}</td>
                    {/* one malformed answer must not break the whole executions table */}
                    <td className="py-0.5">{typeof a.confidence === 'number' ? a.confidence.toFixed(2) : '–'}</td>
                  </>
                  : <td colSpan={2} className="py-0.5 text-red-400 italic">refused</td>}
              </tr>
            ))}
          </tbody>
        </table>
        {shown.length < answers.length && <div className="text-gray-400 mt-1">+{answers.length - shown.length} more</div>}
      </div>
    )
  }

  return (answers.length
    ? <DataCellWrapper rawData={answers} expand={expand} renderReadable={renderAnswers(true)}>
      {renderAnswers(expand ?? false)}
    </DataCellWrapper>
    : <span className="text-gray-400 italic">No answers</span>
  )
}

export function JsonCell ({ data, expand }: { data: any, expand?: boolean }) {
  if (!data) return <span className="text-gray-400 italic">Empty</span>
  const json = expand ? JSONHelper.unescapeNewlinesDeep(JSON.stringify(data, null, 2)) : JSON.stringify(data, null, 2)
  return (
    <DataCellWrapper rawData={data} expand={expand}>
      <pre
        className={`text-xs bg-gray-900 p-2 rounded ${!expand ? 'overflow-auto max-w-[300px] max-h-[150px]' : ''} whitespace-pre-wrap break-all custom-scrollbar`}>
        {json}
      </pre>
    </DataCellWrapper>
  )
}

export function ObjCell ({ data, expand }: { data: any, expand?: boolean }) {
  if (!data) return <span className="text-gray-400 italic">Empty</span>

  const renderEntries = (isExpanded: boolean) => (
    <div className={`${!isExpanded ? 'overflow-auto max-w-[300px] max-h-[150px]' : ''} text-xs font-mono flex flex-col ${isExpanded ? 'gap-2' : 'gap-1.5 p-1'}`}>
      {Object.entries(data).map(([key, value]) => (
        <div className={`${isExpanded ? 'p-3' : 'p-1.5'} rounded border border-gray-700`} key={key}>
          <span className="font-bold text-blue-400">{key}</span>
          <div className={`whitespace-pre-wrap break-all ${isExpanded ? 'mt-1' : 'mt-0.5'}`}>
            {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
          </div>
        </div>
      ))}
    </div>
  )

  return (
    <DataCellWrapper rawData={data} expand={expand} renderReadable={renderEntries(true)}>
      {renderEntries(expand ?? false)}
    </DataCellWrapper>
  )
}

export function EditableJsonCell ({ initialValue, onSave }: {
  initialValue: any,
  onSave: (val: any) => Promise<void>
}) {
  const [value, setValue] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    const val = initialValue ? JSONHelper.unescapeNewlinesDeep(JSON.stringify(initialValue, null, 2)) : '{}'
    setValue(val)
  }, [initialValue])

  const handleBlur = async () => {
    if (saving) return
    try {
      // Basic check: if empty, maybe assume {}? Or allow empty?
      // If user clears it, maybe set to null?
      // Let's assume valid JSON is required.
      if (!value.trim()) {
        // If empty string, maybe revert or set to empty obj?
        // Let's parse as empty obj
        setValue('{}')
        setSaving(true)
        await onSave({})
        setSaved(true)
        setTimeout(() => setSaved(false), 2000)
        return
      }

      const escaped = JSONHelper.escapeNewlinesInsideJsonStrings(value)
      const parsed = JSON.parse(escaped)

      setSaving(true)
      setError(null)
      await onSave(parsed)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch (e) {
      setError('Invalid JSON')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="relative group">
      <textarea
        className={`text-xs bg-gray-900 p-2 rounded overflow-auto w-[300px] whitespace-pre font-mono field-sizing-content resize-none border ${error ? 'border-red-500' : 'border-transparent focus:border-blue-500'}`}
        value={value}
        onChange={e => {
          setValue(e.target.value)
          setError(null)
          setSaved(false)
        }}
        onBlur={handleBlur}
        disabled={saving}
      />
      {error && <div
        className="absolute bottom-0 right-0 bg-red-900 text-red-200 text-[10px] px-1 pointer-events-none">{error}</div>}
      {saving &&
        <div className="absolute top-0 right-0 text-blue-400 text-[10px] p-1 pointer-events-none">Saving...</div>}
      {saved && !saving &&
        <div className="absolute top-0 right-0 text-green-400 text-[10px] p-1 pointer-events-none">Saved</div>}
    </div>
  )
}
